// frontend/src/components/concepts/AnalyzeToolNest.tsx
// Analyze phase nest in the ConceptRail (Piece C). Rows OPEN tools — a rail
// click never starts an LLM job; running lives in the panel / Overview.
import { useNavigate } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { useAnalyzeToolState } from "@/hooks/useAnalyzeToolState";
import { useAnalyzeRecs } from "@/hooks/useAnalyzeRecs";
import { analyzeFrontierTool } from "@/lib/analyzePipelineState";
import { STATUS_BADGE, TOOL_META, TOOL_ORDER } from "@/components/analyze/toolMeta";
import type { ToolId } from "@/lib/analyzeToolState";
import { useLandscapeStore } from "@/stores/useLandscapeStore";
import { trackEvent } from "@/lib/engagement";

/** ToolId → ?activeTool= value consumed by analyze.tsx's deep-link effect. */
const PANEL_PARAM: Partial<Record<ToolId, "debate" | "audit" | "chat" | "comparison">> = {
  "field-debate": "debate",
  "coverage-audit": "audit",
  chat: "chat",
  comparison: "comparison",
};

export function AnalyzeToolNest({ conceptId }: { conceptId: string }) {
  const navigate = useNavigate();
  const { statuses, debateNeedsRefresh } = useAnalyzeToolState(conceptId);
  const recs = useAnalyzeRecs(conceptId, { debateNeedsRefresh });

  // The ➤ marks the tool the overview's Next-step card is promoting — same
  // advisor rec, so the rail and the overview never disagree.
  const frontierToolId: ToolId | null = analyzeFrontierTool(recs[0]);

  const onToolClick = (id: ToolId) => {
    trackEvent("analyze_tool_click", { tool: id, from: "rail-nest" });
    if (id === "landscape") {
      useLandscapeStore.getState().open();
      return;
    }
    if (id === "critique") {
      navigate({
        to: "/concepts/$conceptId/build",
        params: { conceptId },
        search: { view: "map", critique: true },
      });
      return;
    }
    navigate({
      to: "/concepts/$conceptId/analyze",
      params: { conceptId },
      // Functional form preserves sibling params (e.g. ?scope= camp scope) —
      // an object literal here would wipe them for a frame until Effect A
      // in analyze.tsx restores them from the store.
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        activeTool: PANEL_PARAM[id],
      }),
    });
  };

  return (
    <div className="ml-4 mb-2 border-l border-border pl-2.5">
      {TOOL_ORDER.map((id) => {
        const meta = TOOL_META[id];
        const { status, lockedReason } = statuses[id];
        const locked = status === "locked";
        return (
          <button
            key={id}
            type="button"
            aria-label={meta.title}
            data-tool={id}
            disabled={locked}
            onClick={() => onToolClick(id)}
            className="block w-full rounded-md px-1.5 py-1 text-left transition-colors hover:bg-accent/40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <div className="flex items-center gap-1.5">
              <span
                className={`inline-flex size-4 shrink-0 items-center justify-center rounded ${locked ? "opacity-50" : ""}`}
                style={{ background: `${meta.iconColor}29`, color: meta.iconColor }}
              >
                {meta.icon}
              </span>
              {frontierToolId === id && (
                <span
                  aria-label="Next step"
                  className="shrink-0 text-[10px] font-bold"
                  style={{ color: "#cba6f7" }}
                >
                  ➤
                </span>
              )}
              <span
                className={`min-w-0 truncate text-[11px] ${locked ? "text-muted-foreground/70" : "text-foreground"}`}
              >
                {meta.title}
              </span>
              {meta.handoff && !locked && (
                <ArrowUpRight className="size-3 shrink-0 text-muted-foreground/70" />
              )}
              <span
                className={`ml-auto shrink-0 rounded px-1 py-px text-[8px] font-bold tracking-wider ${STATUS_BADGE[status].className}`}
              >
                {STATUS_BADGE[status].label}
              </span>
            </div>
            {locked && lockedReason && (
              <div className="mt-0.5 pl-[22px] text-[9px] leading-tight text-muted-foreground/70">
                🔒 {lockedReason}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
